import React, { ReactNode, useEffect, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import {
  CATEGORIA_AZIENDE_CODICE,
  CATEGORIA_MOBILITA_SOSTENIBILE_CODICE,
  CATEGORIA_PRIVATI_CODICE,
} from '@next-nx/utils/settings'
import { useTrans, useTealium } from '@next-nx/hooks'
import { MenuItemType, OffertaType } from '@next-nx/utils/types'
import { getTimestamp } from '@next-nx/utils'
import { ArrowRight } from '@next-nx/shared-ui/icons'
import { Button, TabGroup, WContainer } from '@next-nx/shared-ui/atoms'
import { OffertaCard } from '@next-nx/shared-ui/molecules'
import styles from './ComparatoreSuggerimenti.module.sass'

interface Props {
  item: MenuItemType | undefined
  addToCompare: (o: OffertaType) => void
  className?: string
}

interface TabContentProps {
  codiceCategoriaPrimaria: string
  offerte: OffertaType[]
  addToCompare: (o: OffertaType) => void
  cta?: string
}

const ComparatoreSuggerimentiTabContent = (props: TabContentProps) => {
  const { codiceCategoriaPrimaria, addToCompare = () => undefined, offerte = [], cta = '' } = props

  const t = useTrans()
  const { linkDataLayer } = useTealium()

  const handleTracking = (product: string = '') => {
    const action_timestamp = getTimestamp()

    linkDataLayer({
      virtual: 'action',
      action_name: 'comparatore',
      action_tech: 'click',
      action_type: 'lasciati_ispirare_compara',
      action_timestamp,
      action_effect: 'aggiunta_prodotto_comparatore',
      cta: cta,
      product: product.replace(/ /g, '_').replace(/\./g, '').toLowerCase(),
    })
  }

  return (
    <div className={styles.tabContent}>
      <Swiper
        slidesPerView={1.2}
        spaceBetween={30}
        breakpoints={{
          576: {
            slidesPerView: 1.8,
          },
          768: {
            slidesPerView: 2.5,
          },
          992: {
            slidesPerView: 3,
          },
        }}
      >
        {offerte.map((x) => (
          <SwiperSlide key={x.pk}>
            <div className={styles.suggerimento}>
              <OffertaCard
                offerta={x}
                codiceCategoriaPrimaria={codiceCategoriaPrimaria}
                className={styles.suggerimentoCard}
              />
              <div className={styles.suggerimentoActions}>
                <Button
                  label={t('Compara')}
                  onClick={() => {
                    addToCompare(x)
                    handleTracking(x?.nome)
                  }}
                />
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  )
}

const ComparatoreSuggerimenti = (props: Props) => {
  const { item = undefined, addToCompare = () => undefined, className = '' } = props

  const t = useTrans()
  const { linkDataLayer, getListingProdottiData } = useTealium()

  const privatiItem =
    (item?.children &&
      item.children.edges.find((x) => x.node.key === CATEGORIA_PRIVATI_CODICE)?.node) ||
    undefined
  const businessItem =
    (item?.children &&
      item.children.edges.find((x) => x.node.key === CATEGORIA_AZIENDE_CODICE)?.node) ||
    undefined
  const mobilitaSostenibileItem =
    (item?.children &&
      item.children.edges.find((x) => x.node.key === CATEGORIA_MOBILITA_SOSTENIBILE_CODICE)
        ?.node) ||
    undefined

  const privatiOfferte =
    privatiItem?.children?.edges.map((x) => x.node.offerta).filter((x) => !!x) || []
  const businessOfferte =
    businessItem?.children?.edges.map((x) => x.node.offerta).filter((x) => !!x) || []
  const mobilitaSostenibileOfferte =
    mobilitaSostenibileItem?.children?.edges.map((x) => x.node.offerta).filter((x) => !!x) || []

  const tabs: {
    key: string
    label?: string
    disabled?: boolean
    content: ReactNode
  }[] = [
    ...(privatiItem && privatiOfferte.length > 0
      ? [
          {
            key: privatiItem.key || CATEGORIA_PRIVATI_CODICE,
            label: privatiItem.name,
            content: (
              <ComparatoreSuggerimentiTabContent
                addToCompare={addToCompare}
                offerte={privatiOfferte}
                codiceCategoriaPrimaria={CATEGORIA_PRIVATI_CODICE}
                cta="popolari_tra_i_privati"
              />
            ),
          },
        ]
      : []),
    ...(businessItem && businessOfferte.length > 0
      ? [
          {
            key: businessItem.key || CATEGORIA_AZIENDE_CODICE,
            label: businessItem.name,
            content: (
              <ComparatoreSuggerimentiTabContent
                addToCompare={addToCompare}
                offerte={businessOfferte}
                codiceCategoriaPrimaria={CATEGORIA_AZIENDE_CODICE}
                cta="popolari_tra_business"
              />
            ),
          },
        ]
      : []),
    ...(mobilitaSostenibileItem && mobilitaSostenibileOfferte.length > 0
      ? [
          {
            key: mobilitaSostenibileItem.key || CATEGORIA_MOBILITA_SOSTENIBILE_CODICE,
            label: mobilitaSostenibileItem.name,
            content: (
              <ComparatoreSuggerimentiTabContent
                addToCompare={addToCompare}
                offerte={mobilitaSostenibileOfferte}
                codiceCategoriaPrimaria={CATEGORIA_MOBILITA_SOSTENIBILE_CODICE}
                cta="popolari_in_mobilità_sostenibile"
              />
            ),
          },
        ]
      : []),
  ]

  const [activeTab, setActiveTab] = useState(CATEGORIA_PRIVATI_CODICE)

  const ctaItem = activeTab === CATEGORIA_AZIENDE_CODICE ? businessItem : privatiItem

  const handleTracking = () => {
    const action_timestamp = getTimestamp()

    linkDataLayer({
      virtual: 'action',
      action_name: 'items_view',
      action_tech: 'view',
      action_type: 'carosello',
      action_timestamp,
      action_effect: 'visualizzazione_prodotti',
      view_items: getListingProdottiData(
        activeTab === CATEGORIA_PRIVATI_CODICE
          ? privatiOfferte
          : activeTab === CATEGORIA_AZIENDE_CODICE
          ? businessOfferte
          : mobilitaSostenibileOfferte,
        activeTab,
        'altre_proposte'
      ),
    })
  }

  useEffect(() => {
    handleTracking()
  }, [activeTab])

  return item ? (
    <div className={`${styles.root} ${className}`}>
      <WContainer>
        <div className={styles.layout}>
          {!!item.name && <p className={styles.title}>{item.name}</p>}
        </div>
        <div className={styles.tabs}>
          <TabGroup
            tabs={tabs}
            triggersClassName={styles.tabsTriggers}
            setExternalActiveTab={setActiveTab}
            triggerSlider
          />
          {ctaItem?.ctaLabel && ctaItem?.url && (
            <div className={styles.ctaBox}>
              <Button
                label={ctaItem.ctaLabel}
                href={ctaItem.url}
                variant="ghost"
                icon={<ArrowRight />}
                iconPos="right"
              />
            </div>
          )}
        </div>
      </WContainer>
    </div>
  ) : (
    <></>
  )
}

export default ComparatoreSuggerimenti
